import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHero, PageBody, formatDate, slugify } from "./_shared";
import { useFood } from "./FoodLayout";
import { ImageIcon, Trophy, Calendar } from "lucide-react";

export default function CampusEngagements() {
  const data = useFood();
  const { typeSlug } = useParams();
  const navigate = useNavigate();
  const all = data.events || [];
  const list = useMemo(() => {
    if (!typeSlug) return all;
    return all.filter((e) => slugify(e.event_type || "") === typeSlug.toLowerCase());
  }, [all, typeSlug]);
  const activeTypeLabel = typeSlug ? list[0]?.event_type || typeSlug : "";

  const [activeId, setActiveId] = useState(list[0]?.id || null);
  useEffect(() => { setActiveId(list[0]?.id || null); }, [typeSlug, list]);
  const active = list.find((e) => e.id === activeId) || null;

  return (
    <div data-testid="ft-engagements">
      <PageHero eyebrow={typeSlug ? `Stories · ${activeTypeLabel}` : "Stories"} title="Campus Engagements" accent="orange" />
      <PageBody>
        {typeSlug && (
          <div className="mb-4 flex items-center justify-between bg-[#FFF1E5] border border-[#F4751F]/30 rounded-md px-4 py-2 text-sm">
            <span className="text-[#0F1F17]">Showing <span className="font-semibold">{activeTypeLabel}</span> ({list.length})</span>
            <button onClick={() => navigate("../campus-engagements")} className="text-[11px] uppercase tracking-wider font-semibold text-[#C95507] hover:text-[#1F6B3F]">Show All</button>
          </div>
        )}
        {list.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center text-sm text-[#4D5A52] ring-1 ring-[#E6E2D6]">
            Campus stories will appear here once published.
          </div>
        ) : (
          <div className="grid md:grid-cols-[300px_1fr] gap-6">
            <aside className="bg-white rounded-lg ring-1 ring-[#E6E2D6] md:sticky md:top-32 self-start overflow-hidden">
              <div className="px-4 py-3 border-b border-[#E6E2D6] text-[10px] uppercase tracking-[0.25em] text-[#F4751F] font-semibold">{typeSlug ? activeTypeLabel : "All"} ({list.length})</div>
              <ul className="divide-y divide-[#E6E2D6] max-h-[600px] overflow-y-auto">
                {list.map((e) => {
                  const isA = e.id === activeId;
                  const isAch = /achievement/i.test(e.event_type || "");
                  return (
                    <li key={e.id}>
                      <button onClick={() => setActiveId(e.id)} className={`w-full text-left px-4 py-3 flex items-start gap-3 ${isA ? "bg-[#FFF1E5]" : "hover:bg-[#FCFAF4]"}`}>
                        <span className={`shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center ${isAch ? "bg-[#F4751F]" : "bg-[#1F6B3F]"} text-white`}>
                          {isAch ? <Trophy size={11} /> : <Calendar size={11} />}
                        </span>
                        <div className="min-w-0 flex-1">
                          {e.event_type && <div className={`text-[9px] tracking-[0.2em] uppercase font-bold mb-0.5 ${isAch ? "text-[#C95507]" : "text-[#1F6B3F]"}`}>{e.event_type}</div>}
                          <div className={`text-sm leading-snug ${isA ? "text-[#0F1F17] font-semibold" : "text-[#4D5A52]"}`}>{e.title}</div>
                          <div className="text-[11px] text-[#4D5A52] mt-0.5">{formatDate(e.event_date)}</div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>
            {active && (
              <article className="bg-white rounded-lg ring-1 ring-[#E6E2D6] p-7 shadow-sm">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#F4751F] font-bold">{active.event_type}</div>
                <h2 className="mt-2 font-serif text-2xl text-[#0F1F17]">{active.title}</h2>
                <div className="mt-1 text-[12px] text-[#4D5A52]">{formatDate(active.event_date)}</div>
                {active.description && <p className="mt-5 text-sm text-[#4D5A52] leading-relaxed whitespace-pre-line">{active.description}</p>}
                {Array.isArray(active.photos) && active.photos.length > 0 && (
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {active.photos.map((p, i) => (
                      <img key={i} src={p.url || p} alt="" className="aspect-video object-cover rounded-md ring-1 ring-[#E6E2D6]" loading="lazy" />
                    ))}
                  </div>
                )}
              </article>
            )}
          </div>
        )}
      </PageBody>
    </div>
  );
}
